import React, { useCallback, useRef, useState } from "react";

import { faCheck, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { CustomPopover } from "src/base-components/Popover";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { useOnClickOutside } from "src/hooks/usehooks-ts";

const BOOLEAN_OPTIONS = [true, false, null] as const;

export const BooleanEditor: React.FC<{
  value: unknown;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  hasLeftIcon?: boolean;
}> = ({ value, onChange, onBlur, hasLeftIcon }) => {
  const hasValue = value !== null && value !== undefined;
  const boolValue = Boolean(value);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(() => {
    if (!hasValue) return 0;
    return boolValue ? 0 : 1;
  });

  const listCallbackRef = useCallback((node: HTMLDivElement | null) => {
    listRef.current = node;
    if (node) requestAnimationFrame(() => node.focus());
  }, []);

  useOnClickOutside([triggerRef, listRef], onBlur);

  const handleSelect = (newValue: boolean | null) => {
    onChange(newValue);
    onBlur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          Math.min(prev + 1, BOOLEAN_OPTIONS.length - 1),
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        handleSelect(BOOLEAN_OPTIONS[focusedIndex]);
        break;
      case "Escape":
        e.preventDefault();
        onBlur();
        break;
      case "Tab":
        // Let Tab propagate to table for navigation
        return;
    }
    e.stopPropagation();
  };

  return (
    <div ref={triggerRef} className="h-full w-full min-w-0">
      <CustomPopover
        button={
          <div
            className={twJoin(
              "flex h-full w-full items-center py-1.5 pr-2",
              hasLeftIcon ? "pl-6" : "pl-1.5",
            )}
          >
            {hasValue && (
              <Pill size="sm" variant={boolValue ? "green" : "red"}>
                <Pill.Text>{boolValue ? "True" : "False"}</Pill.Text>
              </Pill>
            )}
          </div>
        }
        className="w-44"
        isOpen={true}
        offsetX={8}
        placement="bottom-start"
        wrapperClassName="w-full"
      >
        <div
          ref={listCallbackRef}
          className="outline-none"
          role="listbox"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          {([true, false] as const).map((bool, index) => (
            <button
              key={String(bool)}
              aria-selected={hasValue && boolValue === bool}
              className={twJoin(
                "flex w-full items-center justify-between px-4 py-2.5 font-inter-normal-13px text-gray-800",
                focusedIndex === index ? "bg-gray-50" : "hover:bg-gray-50",
              )}
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(bool);
              }}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <Pill size="sm" variant={bool ? "green" : "red"}>
                <Pill.Text>{bool ? "True" : "False"}</Pill.Text>
              </Pill>
              {hasValue && boolValue === bool && (
                <Icon icon={faCheck} size="xs" variant="active" />
              )}
            </button>
          ))}
          <div className="my-1 border-gray-100 border-t" />
          <button
            className={twJoin(
              "flex w-full items-center gap-2 px-4 py-2.5 font-inter-normal-13px text-gray-800",
              focusedIndex === 2 ? "bg-gray-50" : "hover:bg-gray-50",
            )}
            role="option"
            onMouseDown={(e) => {
              e.preventDefault();
              handleSelect(null);
            }}
            onMouseEnter={() => setFocusedIndex(2)}
          >
            <Icon icon={faTrashCan} size="xs" />
            Remove selection
          </button>
        </div>
      </CustomPopover>
    </div>
  );
};
